import type { Messages } from '../messages.js'

/**
 * Nigerian Pidgin, written the way its own Wikipedia writes it.
 *
 * Naija is English-lexified and is not English, and the difference is grammar rather than
 * spelling: `dey` marks the ongoing, `go` marks the future, `no` negates, `wey` relativises,
 * and `na` is the copula. So `tiles dey turn` is not a typo for `tiles are turning` — it is how
 * the sentence is built. Anything here that reads like English with the endings knocked off has
 * been written wrong; the endings were never there.
 *
 * `move` for flips, which is the word the game already uses for the thing you spend.
 */
export const pcm: Messages = {
  tag: 'pcm',

  readingDictionary: 'Dey read di dictionary…',
  noWordList: 'No word list for "{language}". Build one:  pnpm dictionary build',
  emptyWordList: 'Di word list for "{language}" empty.',

  flips: 'moves',
  score: 'score',
  words: 'words',
  round: 'round',
  ticksLeftLabel: 'Time wey remain for dis round',
  typeAWord: 'type word',
  tapPrompt: 'tap letters make you pick or return dem, den {action}',

  boardOfTiles: 'Board wey get {n} tiles',
  faceDown: 'face down',
  wildCard: 'wild card',
  wildKey: 'any letter',
  letterReplaced: '{from} don turn {to}',
  letterSwap: 'LETTER SWAP!',
  spentTile: 'tile wey don spend',
  hiddenWhilePaused: 'dem hide am while e pause',
  letterInWord: '{letter}, letter {position} for di word',

  completeWord: 'Complete di word',

  completeShort: 'Complete',
  reset: 'Clear',
  pause: 'Pause',
  resume: 'Continue',
  newGame: 'New game',
  paused: 'E pause',
  outOfFlips: 'Moves don finish',
  finalResult: '{score} from {words} for {rounds}',
  playAgain: 'Play again',
  share: 'Share',
  shareCopied: 'E don copy.',
  shareSelect: 'Copy dis one:',

  lettersSelect: 'letters dey pick',
  keysWild: 'dem go take am when you type letter wey no tile dey show',
  clearsEvery: 'e go clear every {letter} wey you pick',
  undoLastLetter: 'undo di last letter',
  noWordsYet: 'No word yet.',

  wordRejected: '{word}  {reason}',
  reasonDuplicate: 'you don find am',
  reasonTooShort: 'e too short',
  reasonNotAWord: 'e no be word',
  reasonAllFound: 'you don get all of dem',
  noSuchLetterUp: 'no {letter} dey open',
  nothingUp: 'nothing dey open',
  letterHidden: 'one letter go hide',
  shuffled: 'dem don shuffle am',
  shuffledAndBilled: 'dem shuffle am, and charge {flips} wey you no use',
  tooFewLetters: 'Di letters wey remain too small',

  gameLanguage: 'language',
  interfaceLanguage: 'interface',
  dictionarySize: '{common} everyday for inside {full} words',
  filterLanguages: 'Find language',
  noMatches: 'Nothing match',

  nerdMode: 'nerd mode',
  rules: 'Rules',
  difficulty: 'how e hard',
  difficultyNames: { easy: 'easy', medium: 'medium', hard: 'hard', insane: 'craze' },
  pausedNoBoard: 'Dis game no go show for di leaderboard.',
  about: 'About am',
  themeLabel: 'look',
  themeNames: { traditional: 'di normal one', light: 'bright one', contrast: 'strong contrast' },
  tiles: 'tiles (N)',
  secondsPerTick: 'seconds / tick',
  holdTicks: 'hold ticks',
  minWord: 'shortest word',
  startingFlips: 'moves wey you start with',
  wildChance: 'chance for wild card',
  replaceChance: 'chance for letter swap',
  hideChance: 'chance for letter go hide',
  wordCompleteMode: 'when word complete',
  wordCompleteNames: { shuffle: 'shuffle', spend: 'spend', keep: 'keep' },
  flipEconomy: 'how moves dey come back',
  flipEconomyNames: {
    none: 'none',
    perLetter: 'per letter',
    fibonacci: 'fibonacci',
    overMinimum: 'over di minimum',
  },
  repeatedLetterKey: 'key for letter wey repeat',
  keySchemeNames: { cycle: 'cycle', advance: 'advance' },
  keySchemeHelp: {
    cycle:
      'A go take di next A wey never use, and once all of dem enter di word, e go clear dem. ' +
      'Shift+A go clear dem too.',
    advance: 'A go take di next A wey never use. Shift+A go clear every A for di word.',
  },

  whatThatMeans: 'Wetin dat one mean',
  factRound: 'round',
  factWholeBoardUp: 'di whole board dey open for',
  factRoundCosts: 'one round dey cost',
  factFlipsBuy: 'di moves wey you start with dey buy',
  factThisBoard: 'dis board',
  factBoardHadToAdmit: 'di board suppose accept',
  ticksAndSeconds: '{ticks}, {seconds}',
  wordsLongest: '{words}, di longest na {longest}',
  wordsIncludingOneOf: '{words}, and one of dem na {ceiling}',
  scorelessRounds: '{rounds} wey no score',

  whatAWordPays: 'Wetin one word dey pay',
  columnLetters: 'letters',
  columnCost: 'cost',
  columnPoints: 'points',
  columnFlips: 'moves',
  columnNet: 'net',

  canonicalRules: 'Di normal {difficulty} rules.',
  customRules: 'You don change am from di preset. Score wey use your own rules no go rank.',
  applyAndStart: 'Apply and start new game',
  changesNextGame: 'Di changes go work from di next game.',
  presets: 'Presets:',

  start: 'Start',
  restart: 'Start again',
  quit: 'Quit',
  quitTitle: 'You wan quit dis game?',
  restartTitle: 'You wan start dis game again?',
  restartConfirm: 'Start again',
  quitConfirm: 'Quit',
  keepPlaying: 'Continue to play',
  personalBest: 'Your best games',
  newPersonalBest: 'New personal best.',
  columnRank: '#',
  notRanked: 'Your own rules, so dis game no go rank.',
  notRankedPaused: 'Di clock don stop, so dis game no go rank.',
  rankOfTotal: '{rank} out of {total}',

  howToPlay: 'How to play',

  backToGame: 'Go back to di game',
  welcomeTitle: 'Welcome to Blinkered',
  tutorialSkip: 'Skip',
  tutorialNext: 'Next',
  tutorialBack: 'Back',
  tutorialStart: 'Start to play',
  tutorialHideAgain: 'No show dis again',
  tutorialProgress: '{n} out of {total}',
  tutorialSkipTitle: 'You wan skip di tour?',
  tutGoalTitle: 'Blinkered na race',
  tutGoalBody:
    'You go start di game with fixed number of moves, and every tile wey turn dey cost one move. When di moves finish, di game don end. Word wey you form dey give you moves, and di longer di word, di more moves you go get.',
  tutSpentBody:
    'Letters wey you use for word go face down until di next round. Na only when dem change letter, di letters for di board go change.',
  tutChangeTitle: 'Letters fit change',
  tutSameLetters:
    'You see word wey you no get chance play? Di same letters go come back next round, shuffle enter new places.',
  tutPickLetters: 'Tap di letters wey you want, one after di other, make you form word.',
  tutMoreTurn: 'Tiles dey still turn while you dey think, so better letter fit still dey come.',
  tutTapBack:
    'You tap one wey you no want? Tap am again make e go back. Any one, no be only di last one.',
  tutComplete: 'Press Complete when di word ready.',
  htBoardTitle: 'Di board',
  htBoardBody:
    'Tiles dey turn face up one by one, na random order. You fit take di ones wey open form word.',
  htWordsTitle: 'Di words',
  htWordsBody:
    'Spell word from di tiles wey dey show, by typing or clicking di letters one after di other.',
  htFlipsTitle: 'Di moves',
  htFlipsBody:
    'Every tile wey turn dey cost one move. Word wey you complete dey add moves back, and long word dey pay pass. When di moves finish, di game don end.',
  htRoundTitle: 'Di round',
  htRoundBody:
    'When di last tile of di round turn, di whole board go hold small. Den dem go turn di tiles over and shuffle dem, and new round go start.',
  htLanguagesTitle: 'Di languages',
  htLanguagesBody:
    '{n} of dem. Any board you fit solve with words wey people dey really use. Word wey no common still dey score, if di dictionary sabi am.',
  htKeysTitle: 'Di keyboard',
  htWildTitle: 'Wild cards',
  htWildBody:
    'Sometimes wild card go show instead of letter. Wild card dey count as any letter wey go make correct word. Word wey you don complete before no dey count.',
  htHideTitle: 'Letters wey dey hide',
  htHideBody:
    'For di harder levels, letter wey open fit turn back by itself, and e go come back later inside di same round. Di timer go gain one extra tick for am, wey dey red. Letter wey you don pick no go turn back.',
  tutHideBody:
    'For di harder levels, letter wey open fit turn back by itself, and e go come back later inside di same round. Letter wey you don pick no go turn back.',
  htSwapTitle: 'Letters wey dey change',
  htSwapBody:
    'Sometimes, between rounds, dem go replace one letter with another one. You go see which one comot and which one enter.',
  htLevelsTitle: 'Di levels',
  htLevelEasy:
    'Di same twelve letters throughout di game, so you fit learn dem and carry list of words for your head. Tiles dey turn slow, and di whole board dey show long enough make you finish your picking.',
  htLevelMedium:
    'Letter dey change now and den, so e hard to remember words wey you keep for later. Less time to look, and less time to think.',
  htLevelHard:
    'Three-letter words no dey count again, and letter dey change almost every other round. Di board barely show before e shuffle.',
  htLevelInsane:
    'Everything at once, full speed. Di board dey shuffle almost immediately after di last move.',
  htTouchTitle: 'Di touchscreen',
  htTouchBody:
    'Tap tile wey dey show make you take im letter. Tap any letter wey you don take make e go back. Complete and Clear dey under di board.',

  accountTitle: 'Your account',
  menuProfile: 'My profile',
  menuGames: 'My games',
  menuPublicPage: 'My public page',
  menuSignedInAs: 'You don sign in as',
  signIn: 'Sign in',
  signOut: 'Sign out',
  signInTitle: 'Sign in or open account',
  signInLead: 'Account dey keep your games, for any device wey you dey play for.',
  signInKeepGame: 'Sign in make dis game stay, and every one wey go follow.',
  continueWithApple: 'Continue with Apple',
  continueWithGoogle: 'Continue with Google',
  signInOr: 'abi',
  emailLabel: 'Email',
  codeLabel: 'Code',
  emailMeACode: 'Send me code',
  working: 'Small time…',
  sendNewCode: 'Send new code',
  codeSent: 'Check your email. Di code go last 10 minutes.',
  notNow: 'Not now',
  badEmail: 'Dis one no be email address.',
  badCode: 'Dat code no work. Codes dey expire after 10 minutes and dem dey work one time.',
  serverBusy: 'We no fit reach di server. Try again small time.',
  serverDown: 'We no fit reach di server.',
  offline: 'Net no dey',
  leaderboardTitle: 'Leaderboard',
  leaderboardEmpty: 'No game dey dis board yet. Be di first one.',
  leaderboardWouldBe: 'Where dis game go stand',
  leaderboardThisGame: 'Dis game',
  leaderboardRanksHere: 'Where dis game dey stand',
  leaderboardOutside: 'Outside di top five.',
  tutAccountTitle: 'Keep your score',
  tutAccountBody:
    'Account go save your game, put your score on di leaderboard, and hold your profile.',
  ssoCancelled: 'Dem cancel di sign in.',
  ssoExpired: 'E take too long. Sign in again.',
  ssoNoUsername: 'We no fit finish create di account. Try again small time.',
  ssoFailed: 'E no work. Try again, abi use your email.',
  tabProfile: 'Profile',
  countryAny: 'I no go talk',
  findCountry: 'Find country',
  usernameLabel: 'Username',
  bioLabel: 'About me',
  countryLabel: 'Country',
  bioHint: 'No links. {left}/{max}',
  save: 'Save',
  saving: 'Dey save…',
  saved: 'Dem save am.',
  nameTaken: 'Person don get dat name.',
  nameTooShort: 'Make e long small.',
  nameTooLong: 'E too long.',
  nameBadCharacters: 'Na letters, numbers, hyphen and underscore only.',
  nameBadEdges: 'E must start and end with letter or number.',
  nameMixedScripts: 'Pick one alphabet and stay for am.',
  nameReserved: 'Dis one be like name wey we dey give. Pick another one.',
  nameUnusable: 'Dat name no fit work.',
  bioTooLong: 'Na {max} characters e fit take.',
  bioHasLink: 'Links no dey allowed here.',
  bioHasControl: 'E get characters wey no fit stay here.',
  bioUnusable: 'Dat text no fit work.',
  gamesHeading: 'Games',
  gamesEmpty: 'Nothing dey here yet. Games wey you play when you sign in go show here.',
  gamesEmptyShort: 'Nothing dey here yet.',
  gamesLoading: 'Dey read your games…',
  theirGamesLoading: 'Dey read dia games…',
  profileLoading: 'Dey read di profile…',
  playerNotFound: 'Nobody dey here with dat name.',
  columnWhen: 'When',
  columnRounds: 'rounds',
  columnGame: 'Game',
  gameLoading: 'Dey read di game…',
  gameUnreadable: 'We no fit read dis game.',
  gameNotRanked: 'Dem play am with rules wey change, so e no dey ranked.',
  detailNotKept: 'Dem no dey keep di words and boards for dis game again.',
  openingBoard: 'Di first board',
  roundByRound: 'Round by round',
  roundNothing: 'nothing show',
  changeReplaced: 'Dem change {letter} for slot {slot}',
  changeWildOn: '{letter} be wild card for slot {slot}',
  changeWildOff: '{letter} wild card comot from slot {slot}',
  keepThisGame: 'Keep dis game',
  savedToYourGames: 'Dem save am for your games.',

  reportThis: 'Report',
  reportHeading: 'Report dis one',
  reportTheName: 'Di name',
  reportTheBio: 'Di bio',
  reportTheScore: 'Di score',
  reportWhy: 'Wetin bad about am? You no need talk am.',
  reportSend: 'Send di report',
  reportSent: 'Thank you. Somebody go look am.',
  reportSignIn: 'Sign in make you fit report something.',
  reportGone: 'Dat one no dey again.',
  reportCancel: 'Cancel',

  deleteAccount: 'Delete my account',
  deleteAccountWhat:
    'Dis one go remove your profile and every game you keep, finish. You no go be able to bring am back.',
  deleteAccountAsk: 'Send me code',
  deleteConfirm: 'Delete am finish',
  deleteGone: 'Your account done go. Thank you for playing.',
  deleteNoAddress:
    'No confirmed address dey on dis account, so we no fit check di delete. Talk to us and we go do am by hand.',

  rulesetFacts: 'Word wey get {letters} · new tile every {seconds}',
  plurals: {
    words: { one: '{n} word', other: '{n} words' },
    rounds: { one: '{n} round', other: '{n} rounds' },
    flips: { one: '{n} move', other: '{n} moves' },
    ticks: { one: '{n} tick', other: '{n} ticks' },
    points: { one: '{n} point', other: '{n} points' },
    minLetters: { one: '{n} letter or pass', other: '{n} letters or pass' },
    seconds: { one: '{n} second', other: '{n} seconds' },
  },
}
